import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const API_URL = process.env.REACT_APP_API_URL;

  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [lastAuthError, setLastAuthError] = useState(null); // 'unauthorized' | 'server' | 'network' | null
  const booted = useRef(false); // évite le double-run en StrictMode

  const hasToken = () => !!localStorage.getItem('prae.jwt');

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('prae.jwt');
    if (!token) {
      setUser(null);
      setLastAuthError(null);
      setReady(true);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setLastAuthError(null);
      } else if (res.status === 401 || res.status === 403) {
        // seulement ici on purge
        localStorage.removeItem('prae.jwt');
        setUser(null);
        setLastAuthError('unauthorized');
      } else {
        // 404/500/CORS … -> on NE purge PAS le token
        console.warn('loadUser non-OK:', res.status);
        setLastAuthError('server');
      }
    } catch (err) {
      console.error('loadUser error:', err);
      setLastAuthError('network');
      // ne pas purger le token
    } finally {
      setReady(true);
    }
  }, [API_URL]);

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    loadUser();
  }, [loadUser]);

  async function loginWithCredential(credential) {
    const res = await fetch(`${process.env.REACT_APP_PUBLIC_URL}/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body: new URLSearchParams({ credential }).toString(),
    });

    if (!res.ok) {
      let msg = 'Échec de la connexion';
      try {
        msg = (await res.json())?.error || msg;
      } catch {}
      throw new Error(msg);
    }

    const { token, user: userData } = await res.json();
    localStorage.setItem('prae.jwt', token);
    setUser(userData);
    setLastAuthError(null);
  }

  function logout() {
    localStorage.removeItem('prae.jwt');
    setUser(null);
    setLastAuthError(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        ready,
        lastAuthError,
        hasToken,
        loginWithCredential,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
